import React, { useState, useMemo } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import Modal from "../components/modals/Modal";
import { IconQuestionMark, IconShieldCheck } from "@tabler/icons-react";

const formatPrice = (price) =>
    new Intl.NumberFormat("hu-HU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(price) || 0) + " Ft";

const sumBy = (items, key) => items.reduce((sum, item) => sum + (Number(item?.[key]) || 0), 0);

/**
 * Térítési díj összesítő.
 * A díjat a szerver számolja (ElitGatewayDataLib::__components_mapper):
 *  - teljes összegű támogatásnál (közgyógy, üzemi baleset) 0 Ft, itt nincs díjtábla és nincs megerősítő kérdés,
 *  - normatívnál / privátnál tételenként teljes ár, támogatás és fizetendő.
 */
const TeritesiDij = () => {
    const { order } = useOrder();
    const navigate = useStepNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isFullPriceAid = Number(order.hmeliorder?.hmeliorder_specaidtype_is_fullpriceaid) === 1;
    const specAidTypeName = order.hmeliorder?.hmeliorder_specaidtype_national || "";

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleNext = () => {
        setIsModalOpen(false);
        navigate("/Kerdesek");
    };

    const handleModify = () => {
        setIsModalOpen(false);
        navigate("/OsszesitoTablazat");
    };

    const handleBackClick = () => {
        navigate("/OsszesitoTablazat");
    };

    const venyekWithTotals = useMemo(() => {
        return (order.venyek || []).map((veny) => {
            const sortedComponents = veny.components
                ? [...veny.components].sort((a, b) => {
                      if (a.aidfinance_szam < b.aidfinance_szam) return -1;
                      if (a.aidfinance_szam > b.aidfinance_szam) return 1;
                      return 0;
                  })
                : [];

            return {
                ...veny,
                components: sortedComponents,
                totalFull: sumBy(sortedComponents, "component_full_price"),
                totalAid: sumBy(sortedComponents, "component_aid_amount"),
                totalPrice: sumBy(sortedComponents, "component_computed_price"),
            };
        });
    }, [order.venyek]);

    const grandTotal = useMemo(() => sumBy(venyekWithTotals, "totalPrice"), [venyekWithTotals]);

    const sideLabel = (component) => (component.is_left === 1 ? "Bal láb" : "Jobb láb");

    if (isFullPriceAid) {
        return (
            <TablerLayout title="Térítési díj összesítő">
                <div className="row justify-content-center">
                    <div className="col-md-10 mb-4">
                        <div className="card-body">
                            <div className="card border-success shadow-sm rounded-4">
                                <div className="card-body text-center py-5">
                                    <IconShieldCheck size={64} stroke={1.5} className="text-success mb-3" />
                                    <h3 className="mb-2">{specAidTypeName || "Támogatott"} – 100%-os támogatás</h3>
                                    <p className="fs-5 mb-1">
                                        Térítési díj nincs, a megrendelőnek nem kell fizetnie.
                                    </p>
                                    <p className="text-muted mb-0">Ehhez a rendeléshez számla nem készül.</p>
                                </div>
                            </div>

                            {venyekWithTotals.map((veny, idx) => (
                                <div key={idx} className="mt-4">
                                    <h5 className="fs-5 mb-2">
                                        Vény #{idx + 1}
                                        {veny.veny_receipt && <span className="ms-2 text-muted">({veny.veny_receipt})</span>}
                                    </h5>
                                    <ul className="list-group">
                                        {veny.components.length > 0 ? (
                                            veny.components.map((component, i) => (
                                                <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                                                    <span>
                                                        {component.aidfinance_national}
                                                        <span className="text-muted ms-2">({sideLabel(component)})</span>
                                                    </span>
                                                    <span className="badge bg-success-lt">támogatott</span>
                                                </li>
                                            ))
                                        ) : (
                                            <li className="list-group-item text-muted text-center">Nincs kiválasztott tartozék.</li>
                                        )}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="d-flex justify-content-between mt-4">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                        <StepperNavButton side="right" label="Tovább" onClick={handleNext} />
                    </div>
                </div>
            </TablerLayout>
        );
    }

    return (
        <TablerLayout title="Térítési díj összesítő">
            <div className="row justify-content-center">
                <div className="col-md-10 mb-4">
                    <div className="card-body">
                        {specAidTypeName && (
                            <div className="text-muted mb-3">
                                Támogatás típusa: <strong>{specAidTypeName}</strong>
                            </div>
                        )}
                        {venyekWithTotals.map((veny, idx) => (
                            <div key={idx} className="mb-4">
                                <div className="d-flex align-items-center mb-2">
                                    <h5 className="fs-4 mb-0 me-3">
                                        Vény #{idx + 1}
                                        {veny.veny_receipt && (
                                            <span className="ms-2 fs-5 text-muted" style={{ fontSize: 16 }}>
                                                ({veny.veny_receipt})
                                            </span>
                                        )}
                                    </h5>
                                </div>
                                <table className="table table-bordered table-striped align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center" style={{ width: "46%" }}>Tartozék</th>
                                            <th className="text-center" style={{ width: "18%" }}>Teljes ár</th>
                                            <th className="text-center" style={{ width: "18%" }}>Támogatás</th>
                                            <th className="text-center" style={{ width: "18%" }}>Fizetendő</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {veny.components && veny.components.length > 0 ? (
                                            <>
                                                {veny.components.map((component, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            {component.aidfinance_national}
                                                            <span className="text-muted ms-2">({sideLabel(component)})</span>
                                                            {component.component_is_financed === 0 && (
                                                                <span className="badge bg-warning-lt ms-2" title="A támogatás nincs elfogadva, teljes ár fizetendő">
                                                                    nem támogatott
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-end text-muted">{formatPrice(component.component_full_price)}</td>
                                                        <td className="text-end text-muted">
                                                            {component.component_is_financed === 0 ? "-" : "-" + formatPrice(component.component_aid_amount)}
                                                        </td>
                                                        <td className="text-end fw-semibold">{formatPrice(component.component_computed_price)}</td>
                                                    </tr>
                                                ))}
                                                <tr className="table-primary">
                                                    <td className="fw-bold">Vény összesen:</td>
                                                    <td className="text-end">{formatPrice(veny.totalFull)}</td>
                                                    <td className="text-end">-{formatPrice(veny.totalAid)}</td>
                                                    <td className="text-end fw-bold">{formatPrice(veny.totalPrice)}</td>
                                                </tr>
                                            </>
                                        ) : (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted">
                                                    Nincs kiválasztott tartozék.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        ))}

                        <div className="card bg-light border-primary mt-4 mb-3">
                            <div className="card-body d-flex justify-content-between align-items-center">
                                <h4 className="card-title mb-0">Teljes térítési díj:</h4>
                                <h3 className="card-text text-primary mb-0">{formatPrice(grandTotal)}</h3>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex justify-content-between mt-4">
                    <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                    <StepperNavButton side="right" label="Tovább" onClick={handleOpenModal} />
                </div>
            </div>
            <Modal isOpen={isModalOpen} onClose={handleCloseModal} onSecondary={handleModify} onConfirm={handleNext} title="Térítési díj megerősítése" type="warning" confirmText="Igen" secondaryText="Módosítás" confirmClass="btn btn-primary" secondaryClass="btn btn-danger" showSecondary={true} showCancel={false} icon={<IconQuestionMark size={70} stroke={2} className="text-warning" />}>
                <h4>Térítési díjat megismerte és elfogadta a megrendelő?</h4>
                <p className="mt-3 fs-5">
                    Fizetendő összeg: <span className="text-primary fw-bold">{formatPrice(grandTotal)}</span>
                </p>
            </Modal>
        </TablerLayout>
    );
};

export default TeritesiDij;
